// Pure mappers: Firestore docs → the view model App.tsx renders.
import {
  emptyStats,
  mercyRuleOpportunity,
  type BaseballStats,
  type PlateAppearanceResult,
  type RunnerDestination,
} from "../../shared/domain/gameReducer";
import type { DefensePosition } from "../../shared/domain/defense";
import { FORFEIT_SCORE } from "../domain/gamePresentation";
import type { ArchivedGameStats } from "../domain/baseballStats";
import type {
  ActiveGameViewModel,
  DefenseInningDocument,
  EventModel,
  GameModel,
  PlayerListItem,
  PlayerModel,
  PregameModel,
  PregameViewModel,
  RemoteTimestamp,
} from "../shared/types/contracts";

export const PLAY_LOG_TYPES = new Set([
  "recordPlateAppearance",
  "adjustRunner",
  "removeRunner",
  "addOpponentRun",
  "endHalfInning",
  "attributeRbi",
  "gameActionUndone",
]);

const tsMillis = (timestamp: RemoteTimestamp | undefined): number =>
  timestamp &&
  typeof timestamp === "object" &&
  "toMillis" in timestamp &&
  typeof timestamp.toMillis === "function"
    ? timestamp.toMillis()
    : 0;
const sumRuns = (runs: readonly number[] | null | undefined): number =>
  (runs ?? []).reduce((sum, value) => sum + value, 0);

export function playersToView(docs: PlayerModel[] | null | undefined): {
  players: PlayerListItem[];
  prefs: Record<string, string[]>;
} {
  const sorted = [...(docs || [])].sort(
    (a, b) =>
      tsMillis(a.createdAt) - tsMillis(b.createdAt) ||
      String(a.name).localeCompare(String(b.name)),
  );
  const players = sorted.map((d) => ({
    id: d.id,
    name: d.name,
    num: d.number ?? null,
    active: d.active !== false,
    revision: d.revision ?? 0,
    positionPreferences: d.positionPreferences ?? [],
  }));
  const prefs = Object.fromEntries(
    players.map((p) => [p.id, p.positionPreferences]),
  );
  return { players, prefs };
}

export function pregameToView(
  pregame: PregameModel | null | undefined,
): PregameViewModel {
  if (!pregame) {
    return {
      oppName: "",
      isHome: false,
      openInning: false,
      activeIds: [],
      lineupSlots: [],
      gamePitcher: null,
      defense: null,
      revision: 0,
      selectedScheduleId: null,
      scheduledDate: null,
      timeSlot: null,
      field: null,
      beerDuty: null,
      seasonId: null,
    };
  }
  const activeIds = pregame.activePlayerIds ?? [];
  const lineup = pregame.lineupPlayerIds ?? [];
  const lineupSlots = [
    ...lineup,
    ...Array(Math.max(0, activeIds.length - lineup.length)).fill(null),
  ];
  return {
    oppName: pregame.opponentName ?? "",
    isHome: !!pregame.isHome,
    openInning: !!pregame.openInning,
    activeIds,
    lineupSlots,
    gamePitcher: pregame.gamePitcherId ?? null,
    defense: pregame.defense ?? null,
    revision: pregame.revision ?? 0,
    selectedScheduleId: pregame.selectedScheduleId ?? null,
    scheduledDate: pregame.scheduledDate ?? null,
    timeSlot: pregame.timeSlot ?? null,
    field: pregame.field ?? null,
    beerDuty: pregame.beerDuty ?? null,
    seasonId: pregame.seasonId ?? null,
  };
}

export function gameToView(
  game: GameModel | null | undefined,
): ActiveGameViewModel | null {
  if (!game) return null;
  const state = game.state;
  const mercyOpportunity = mercyRuleOpportunity({
    state,
    isHome: game.isHome,
    openInning: game.openInning,
    opponentName: game.opponentName,
    defense: game.defense,
    lineup: game.lineup.flatMap((entry) =>
      entry.playerId
        ? [
            {
              playerId: entry.playerId,
              name: entry.name,
              number: entry.number,
            },
          ]
        : [],
    ),
  });
  return {
    id: game.id,
    status: game.status,
    revision: game.revision ?? 0,
    seasonId: game.seasonId ?? null,
    isHome: !!game.isHome,
    openInning: !!game.openInning,
    oppName: game.opponentName ?? "",
    lineup: game.lineup.map((player) => ({
      id: player.playerId,
      name: player.name,
      num: player.number ?? null,
    })),
    defense: game.defense?.length ? game.defense : null,
    inning: state.inning ?? 1,
    batting: state.batting !== "them",
    outs: state.outs ?? 0,
    batterIdx: state.batterIndex ?? 0,
    bases: state.bases ?? [null, null, null],
    ourRunsByInning: state.ourRunsByInning ?? [0],
    oppRunsByInning: state.opponentRunsByInning ?? [0],
    stats: state.statsByPlayerId ?? {},
    lastBatterId: state.lastBatterId ?? null,
    gameFinal: !!state.final,
    mercyRuleWinner: state.mercyRuleWinner ?? null,
    mercyRuleSide: mercyOpportunity?.side ?? null,
    mercyRuleRunsRemaining: mercyOpportunity?.runsRemaining ?? null,
    mercyRuleRunsNeededToPrevent: mercyOpportunity?.runsNeededToPrevent ?? null,
    forfeitWinner: game.forfeitWinner ?? null,
    undoStack: game.undoStack ?? [],
    scheduleId: game.scheduleId ?? null,
    scheduledDate: game.scheduledDate ?? null,
    timeSlot: game.timeSlot ?? null,
    field: game.field ?? null,
    beerDuty: game.beerDuty ?? null,
    playoffSeriesId: game.playoffSeriesId ?? null,
    seriesGameNumber: game.seriesGameNumber ?? null,
  };
}

export function archivedGameToView(game: GameModel): ArchivedGameStats {
  const state = game.state;
  const us = state.ourRunsByInning ?? [];
  const them = state.opponentRunsByInning ?? [];
  const archivedAt = game.archivedAt;
  const date =
    archivedAt &&
    typeof archivedAt === "object" &&
    "toDate" in archivedAt &&
    typeof archivedAt.toDate === "function"
      ? archivedAt.toDate().toISOString().slice(0, 10)
      : "";
  const stats: Record<string, BaseballStats> = {};
  const playerStats: NonNullable<ArchivedGameStats["playerStats"]> = [];
  game.lineup.forEach((player) => {
    const resolved = player.playerId
      ? state.statsByPlayerId[player.playerId]
      : null;
    const legacy = game.legacyStatsByName?.[player.name];
    const line =
      resolved ?? (legacy ? { ...emptyStats(), ...legacy } : emptyStats());
    stats[player.name] = line;
    playerStats.push({
      playerId: player.playerId,
      name: player.name,
      stats: line,
    });
  });
  // Migrated games may carry stats for names that could not be resolved to a
  // roster player — keep them visible under their historical name.
  for (const [name, legacyStats] of Object.entries(
    game.legacyStatsByName ?? {},
  )) {
    if (!(name in stats)) {
      const line = { ...emptyStats(), ...legacyStats };
      stats[name] = line;
      playerStats.push({ playerId: null, name, stats: line });
    }
  }
  const forfeitWinner = game.forfeitWinner ?? null;
  return {
    id: game.id,
    status: game.status,
    seasonId: game.seasonId ?? null,
    date,
    scheduledDate: game.scheduledDate ?? null,
    timeSlot: game.timeSlot ?? null,
    field: game.field ?? null,
    isHome: !!game.isHome,
    beerDuty: game.beerDuty ?? null,
    scheduleId: game.scheduleId ?? null,
    playoffSeriesId: game.playoffSeriesId ?? null,
    seriesGameNumber: game.seriesGameNumber ?? null,
    opponent: game.opponentName ?? "Opponent",
    ourScore: forfeitWinner
      ? forfeitWinner === "us"
        ? FORFEIT_SCORE
        : 0
      : sumRuns(us),
    oppScore: forfeitWinner
      ? forfeitWinner === "us"
        ? 0
        : FORFEIT_SCORE
      : sumRuns(them),
    forfeitWinner,
    lineup: game.lineup.map((player) => player.name),
    stats,
    playerStats,
    lineScore: { us, them },
  };
}

export function eventsToLog(events: EventModel[] | null | undefined): string[] {
  return (events ?? [])
    .filter((e) => PLAY_LOG_TYPES.has(e.type))
    .sort((a, b) => (b.committedRevision ?? 0) - (a.committedRevision ?? 0))
    .map((e) => e.summary);
}

// Mirrors the server's setDefenseAssignment meta recompute — used for MID-GAME
// edits, which are written wholesale via updateLiveSetup{defense}.
export function manualAssign(
  defense: DefenseInningDocument[] | null | undefined,
  inning: number,
  position: DefensePosition,
  playerId: string | null,
  activeIds: string[],
  prefs: Record<string, readonly string[]>,
): DefenseInningDocument[] {
  return (defense ?? []).map((inn, i) => {
    const assignment = { ...inn.assignment };
    if (i !== inning)
      return {
        ...inn,
        assignment,
        sitters: [...(inn.sitters || [])],
        oop: [...(inn.oop || [])],
      };
    if (playerId) {
      Object.keys(assignment).forEach((pos) => {
        if (assignment[pos] === playerId) delete assignment[pos];
      });
      assignment[position] = playerId;
    } else {
      delete assignment[position];
    }
    const onField = Object.values(assignment);
    const sitters = activeIds.filter((id) => !onField.includes(id));
    const oop = Object.entries(assignment)
      .filter(([pos, id]) => !(prefs[id] || []).includes(pos))
      .map(([, id]) => id);
    return { assignment, sitters, oop };
  });
}

// Standard advancement defaults per result — the play panel pre-fills these.
export function defaultDecisions(
  result: PlateAppearanceResult,
  bases: readonly (string | null)[],
): Record<number, RunnerDestination> {
  const decisions: Record<number, RunnerDestination> = {};
  ([0, 1, 2] as const).forEach((base) => {
    if (!bases[base]) return;
    if (result.key === "HR") {
      decisions[base] = "H";
      return;
    }
    if (result.walk) {
      if (base === 0) decisions[0] = 1;
      else if (base === 1) decisions[1] = bases[0] ? 2 : 1;
      else decisions[2] = bases[0] && bases[1] ? "H" : 2;
      return;
    }
    if (result.sf) {
      decisions[base] = base === 2 ? "H" : base;
      return;
    }
    if (result.out || result.key === "FC") {
      decisions[base] = base;
      return;
    }
    const destination = base + (result.bases || 1);
    decisions[base] = destination >= 3 ? "H" : destination;
  });
  return decisions;
}

const KEEP_APART = ["Gabe", "Drew"];

// OPS-based lineup suggestion from season stats (shrunk toward team average).
// Returns ordered player ids, or null when there is no season data.
type LineupCandidate = Pick<PlayerListItem, "id" | "name">;

type LineupTotals = Pick<
  BaseballStats,
  "AB" | "H" | "1B" | "2B" | "3B" | "HR" | "BB" | "SF"
>;

export function suggestLineupOrder(
  activePlayers: LineupCandidate[],
  gameArchive: ArchivedGameStats[] | null | undefined,
): string[] | null {
  if (!gameArchive || !gameArchive.length) return null;
  const totals: Record<string, LineupTotals> = {};
  gameArchive.forEach((g) => {
    const lines =
      g.playerStats ??
      Object.entries(g.stats).map(([name, stats]) => ({
        playerId: null,
        name,
        stats,
      }));
    lines.forEach(({ playerId, name, stats: gameStats }) => {
      const key = playerId ? `player:${playerId}` : `name:${name}`;
      if (!totals[key])
        totals[key] = {
          AB: 0,
          H: 0,
          "1B": 0,
          "2B": 0,
          "3B": 0,
          HR: 0,
          BB: 0,
          SF: 0,
        };
      const line = totals[key];
      if (!line) return;
      (Object.keys(line) as Array<keyof LineupTotals>).forEach((stat) => {
        line[stat] += gameStats[stat] ?? 0;
      });
    });
  });
  const opsOf = (line: LineupTotals): { ops: number; pa: number } | null => {
    const pa = line.AB + line.BB + line.SF;
    if (!pa || !line.AB) return null;
    const obp = (line.H + line.BB) / pa;
    const totalBases =
      line["1B"] + 2 * line["2B"] + 3 * line["3B"] + 4 * line.HR;
    return { ops: obp + totalBases / line.AB, pa };
  };
  const all = Object.values(totals)
    .map(opsOf)
    .filter((value): value is { ops: number; pa: number } => value !== null);
  const teamOps = all.length
    ? all.reduce((a, x) => a + x.ops, 0) / all.length
    : 0.8;
  const K = 10;
  const scored = activePlayers
    .map((p) => {
      const t = totals[`player:${p.id}`] ?? totals[`name:${p.name}`];
      const o = t ? opsOf(t) : null;
      const score = o
        ? (o.ops * o.pa + teamOps * K) / (o.pa + K)
        : teamOps - 0.001;
      return { p, score };
    })
    .sort((a, b) => b.score - a.score);

  const n = scored.length;
  const slotOrder: number[] = [];
  for (let i = 0; i < Math.min(4, n); i++) slotOrder.push(i);
  const rest: number[] = [];
  for (let i = 4; i < n; i++) rest.push(i);
  const evens = rest.filter((_, i) => i % 2 === 1);
  const odds = rest.filter((_, i) => i % 2 === 0);
  slotOrder.push(...evens, ...odds);

  const order = new Array<string>(n);
  scored.forEach((entry, rank) => {
    const slot = slotOrder[rank];
    if (slot !== undefined) order[slot] = entry.p.id;
  });

  const idxOf = (name: string): number =>
    order.findIndex(
      (id) => activePlayers.find((player) => player.id === id)?.name === name,
    );
  const cyclicDistance = (
    left: number,
    right: number,
    length: number,
  ): number => {
    const distance = Math.abs(left - right);
    return Math.min(distance, length - distance);
  };
  let gi = idxOf(KEEP_APART[0]!);
  const di = idxOf(KEEP_APART[1]!);
  if (gi >= 0 && di >= 0 && cyclicDistance(gi, di, n) < 2) {
    for (let off = 2; off < n; off++) {
      for (const cand of [(gi + off) % n, (gi - off + n) % n]) {
        if (cyclicDistance(cand, di, n) >= 2) {
          [order[gi], order[cand]] = [order[cand]!, order[gi]!];
          gi = cand;
          off = n;
          break;
        }
      }
    }
  }
  return order;
}

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** Short relative timestamp ("just now", "5m ago", "2d ago", "Jul 4") for audit feeds. */
export function timeAgo(
  timestamp: RemoteTimestamp | null | undefined,
  now = Date.now(),
): string {
  if (timestamp == null) return "";
  const ms =
    typeof timestamp === "number"
      ? timestamp
      : timestamp instanceof Date
        ? timestamp.getTime()
        : "toMillis" in timestamp && typeof timestamp.toMillis === "function"
          ? timestamp.toMillis()
          : NaN;
  if (!Number.isFinite(ms)) return "";
  const diff = Math.max(0, now - ms);
  if (diff < 45_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d ago`;
  const d = new Date(ms);
  return `${MONTHS[d.getMonth()] ?? ""} ${d.getDate()}`;
}
